import { useState } from 'react';
import Circle from '@mui/icons-material/Circle';
import Chip from '@mui/material/Chip';
import { DataGrid, GridFooter } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { CircleOutlined } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useMutation, useQueryClient } from 'react-query';
import Slide from '@mui/material/Slide';
import CustomLoading from '../../../shared/CustomLoading';
import { ResendAshlinkUserRegistration } from '../../../../data/apiCalls';
import useSessionContext from '../../../../data/queryHooks/useSessionContext';
import CustomNoResultsOverlay from '../../../shared/CustomNoResultsOverlay';

const actionText = {
	textTransform: 'uppercase',
	fontWeight: 'bold',
};

const SlideLeft = props => <Slide {...props} direction='left' />;

const PendingSubAccountTable = ({ dataRows, isLoading }) => {
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});
	const [resendInviteOpen, setResendInviteOpen] = useState(false);
	const [alertText, setAlertText] = useState({ title: '', description: '' });
	const [openSnackbar, setOpenSnackbar] = useState('');
	const [subAccount, setSubAccount] = useState({});

	const queryClient = useQueryClient();

	const { ashlinkUserId } = useSessionContext();

	const { mutate, isLoading: isFetching } = useMutation({
		mutationFn: values => ResendAshlinkUserRegistration(values),
	});

	const handleClose = reason => {
		if (reason?.type === 'click') {
			return;
		}
		setOpenSnackbar('');
	};

	const errorAlert = () => {
		const text = {
			title: 'Sorry, something went wrong.',
			description: `We were unable to process your request.`,
		};
		setAlertText(text);
		setOpenSnackbar('error');
	};

	const resendInvite = () => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(subAccount?.pendingAshlinkUserEmail)) {
			errorAlert();
			return;
		}
		mutate(subAccount?.pendingAshlinkUserId, {
			onSuccess: () => {
				const email = subAccount?.pendingAshlinkUserEmail;
				setAlertText({
					title: 'Sub Account Creation Invite Sent',
					description: `An email has been sent to ${email} for them to create their account.  The link will expire after 72 hours.`,
				});
				queryClient.invalidateQueries([
					'GetPendingSubAccounts',
					ashlinkUserId,
				]);
				setOpenSnackbar('success');
			},
			onError: () => errorAlert(),
		});
		setResendInviteOpen(false);
	};

	const columns = [
		{
			field: 'pendingAshlinkUserId',
		},
		{
			field: 'pendingAshlinkUserEmail',
			headerName: 'Email',
			flex: 3,
			minWidth: 100,
			valueFormatter: value => {
				if (!value) return '';
				return value;
			},
		},
		{
			field: 'dateSent',
			headerName: 'Date Sent',
			flex: 1,
			minWidth: 140,
			valueFormatter: value => format(new Date(value), 'MM/dd/yy'),
		},
		{
			field: 'status',
			headerName: 'Status',
			flex: 1,
			minWidth: 140,
			renderCell: params => {
				const expired =
					new Date() > new Date(params.row.expirationDate);
				const statusLabel = expired ? 'Expired' : 'Pending';

				return (
					<Chip
						variant='outlined'
						label={statusLabel}
						icon={
							expired ? (
								<Circle color='error' />
							) : (
								<CircleOutlined />
							)
						}
						sx={{ textTransform: 'capitalize' }}
					/>
				);
			},
		},
		{
			field: 'action',
			headerName: 'Actions',
			sortable: false,
			minWidth: 145,
			renderCell: params => (
				<Button
					disableRipple
					onClick={() => {
						setSubAccount(params.row);
						setResendInviteOpen(true);
					}}
					underline='none'
					sx={actionText}
				>
					Resend Invite
				</Button>
			),
		},
	];

	return (
		<>
			<DataGrid
				autoHeight
				rows={dataRows ?? []}
				loading={isLoading}
				columns={columns}
				columnVisibilityModel={{ pendingAshlinkUserId: false }}
				getRowId={row => row.pendingAshlinkUserId}
				rowHeight={58}
				slots={{
					noRowsOverlay: CustomNoResultsOverlay,
					loadingOverlay: CustomLoading,
					footer: GridFooter,
				}}
				slotProps={{
					toolbar: {
						showQuickFilter: dataRows.length > 0,
						quickFilterProps: { debounceMs: 1000 },
					},
					noRowsOverlay: {
						customFailureVerbiage: 'No Pending Sub Accounts',
						customNoResultsVerbiage: 'No Pending Sub Accounts',
					},
				}}
				pagination
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[10, 20, 50]}
				hideFooterSelectedRowCount
				disableMultipleRowSelection
				disableColumnMenu
				disableColumnFilter
				disableColumnResize
				disableColumnSelector
				disableDensitySelector
				sx={{
					'&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
						outline: 'none',
					},
					'& .MuiDataGrid-scrollbarFiller': {
						backgroundColor: 'rgb(0, 46, 110) !important', // eve theme is missing header background color when vertical scrollbar is present
					},
				}}
			/>
			<Dialog
				open={resendInviteOpen}
				onClose={() => setResendInviteOpen(false)}
				disableBackdropClick
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
			>
				<DialogTitle id='alert-dialog-title'>Resend Invite</DialogTitle>
				<DialogContent>
					<Typography id='alert-dialog-description' mt={2}>
						Are you sure you want to resend an invite to this sub
						account?
					</Typography>
					<Grid container columnSpacing={2} mt={2}>
						<Grid container item spacing={3}>
							<Grid item xs={3}>
								<Typography>
									<b>First Name</b>
								</Typography>
							</Grid>
							<Grid item xs={3}>
								<Typography>
									<b>Last Name</b>
								</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>
									<b>Email</b>
								</Typography>
							</Grid>
						</Grid>
						<Grid container item spacing={3}>
							<Grid item xs={3} overflow='hidden'>
								<Typography>{subAccount.firstName}</Typography>
							</Grid>
							<Grid item xs={3} overflow='hidden'>
								<Typography>{subAccount.lastName}</Typography>
							</Grid>
							<Grid item xs={6} overflow='hidden'>
								<Typography>
									{subAccount.pendingAshlinkUserEmail}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ mb: 2 }}>
					<Button
						variant='outlined'
						onClick={() => setResendInviteOpen(false)}
					>
						Cancel
					</Button>
					<Button
						variant='contained'
						onClick={resendInvite}
						disabled={isFetching}
					>
						Resend Invite
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={!!openSnackbar}
				autoHideDuration={10000}
				onClose={reason => handleClose(reason)}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				TransitionComponent={SlideLeft}
			>
				<Alert
					severity={
						['error', 'info', 'success', 'warning'].includes(
							openSnackbar
						)
							? openSnackbar
							: 'info'
					}
				>
					<Typography fontWeight={700}>{alertText.title}</Typography>
					<Typography>{alertText.description}</Typography>
				</Alert>
			</Snackbar>
		</>
	);
};

PendingSubAccountTable.propTypes = {
	dataRows: PropTypes.array.isRequired,
	isLoading: PropTypes.bool.isRequired,
};

export default PendingSubAccountTable;
